import {
  faqExpectations,
  readFaqCollection,
  readFaqPropertyNames,
  replaceFaqItem,
  type FaqCollection,
  type FaqItemReplace,
} from '@/api/core/native-faq-shared';
import type { PropertyFaq } from '@/api/core/native-property-content';
import { Button, Checkbox, Input, Textarea } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { EditActionBar } from './edit-action-bar';
import { allowed } from './presentation/command-owner';
import { useCurrentCommand } from './presentation/use-current-command';
import type { PropertyRuntime } from './property-queries';

type Item = PropertyFaq['faq_sets'][number]['items'][number];
interface Draft {
  questionKo: string;
  questionEn: string;
  answerKo: string;
  answerEn: string;
}
const draftOf = (item: Item): Draft => ({
  questionKo: item.payload.question.ko ?? '',
  questionEn: item.payload.question.en ?? '',
  answerKo: item.payload.answer.ko ?? '',
  answerEn: item.payload.answer.en ?? '',
});
// 비어 있는 입력은 원문 없음(NULL)으로, 값이 있는 입력은 그대로 저장한다.
const orNull = (v: string) => (v === '' ? null : v);

/** 공유 FAQ 항목의 원문 수정. 같은 원문을 쓰는 모든 지점에 한 번에 반영되므로 영향 지점을 보고 확인한 뒤 저장한다. */
export function FaqSharedEditor({
  runtime,
  propertyId,
  collectionId,
  item,
  onSaved,
}: {
  runtime: PropertyRuntime;
  propertyId: string;
  collectionId: string;
  item: Item;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  if (!allowed(runtime, 4)) return null;
  return open ? (
    <Editor
      runtime={runtime}
      propertyId={propertyId}
      collectionId={collectionId}
      item={item}
      onClose={() => {
        setOpen(false);
      }}
      onSaved={onSaved}
    />
  ) : (
    <Button
      variant="outline"
      onClick={() => {
        setOpen(true);
      }}
    >
      공유 원문 수정
    </Button>
  );
}

function Editor({
  runtime,
  propertyId,
  collectionId,
  item,
  onClose,
  onSaved,
}: {
  runtime: PropertyRuntime;
  propertyId: string;
  collectionId: string;
  item: Item;
  onClose: () => void;
  onSaved: () => void;
}) {
  const cmd = useCurrentCommand<FaqCollection, FaqItemReplace>({
    runtime,
    scope: `faq:${collectionId}:${item.id}`,
    bits: 4,
    read: () => readFaqCollection(runtime.client, collectionId),
    write: (body) => replaceFaqItem(runtime.client, collectionId, item.id, body),
    validate: () => Promise.resolve(),
  });
  const names = useQuery({
    queryKey: ['faq-property-names', runtime.apiNamespace, collectionId],
    queryFn: ({ signal }) => readFaqPropertyNames(runtime.client, signal),
    retry: false,
    staleTime: 60_000,
    meta: { suppressGlobalError: true },
  });
  const current = cmd.current?.items.find((candidate) => candidate.id === item.id) ?? null;
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const sent = useRef<Draft | null>(null);
  const saved = useRef(false);
  // 현재 원문을 처음 읽은 뒤, 그리고 저장 뒤 다시 읽은 값으로 입력칸을 맞춘다.
  useEffect(() => {
    if (current && (draft === null || cmd.success)) {
      setDraft(draftOf(current));
      setConfirming(false);
      setAcknowledged(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.revision, cmd.success]);
  useEffect(() => {
    if (cmd.success && !saved.current) {
      saved.current = true;
      onSaved();
    }
    if (!cmd.success) saved.current = false;
  }, [cmd.success, onSaved]);

  const active = cmd.current?.assignments.filter((a) => a.deleted_at === null) ?? [];
  const affected = active.map((a) => ({
    id: a.id,
    name:
      a.property_id.toLowerCase() === propertyId.toLowerCase()
        ? '이 지점'
        : (names.data?.get(a.property_id.toLowerCase()) ?? '이름을 확인하지 못한 지점'),
  }));
  const changed =
    !!current && !!draft && JSON.stringify(draft) !== JSON.stringify(draftOf(current));
  const verified =
    cmd.success && sent.current && draft && JSON.stringify(sent.current) === JSON.stringify(draft);
  const field = (key: keyof Draft, label: string, long = false) => (
    <label className="grid gap-1">
      <span className="body-sm">{label}</span>
      {long ? (
        <Textarea
          value={draft?.[key] ?? ''}
          disabled={cmd.busy}
          onChange={(e) => {
            setDraft({ ...(draft ?? draftOf(item)), [key]: e.target.value });
            setConfirming(false);
            setAcknowledged(false);
          }}
        />
      ) : (
        <Input
          value={draft?.[key] ?? ''}
          disabled={cmd.busy}
          onChange={(e) => {
            setDraft({ ...(draft ?? draftOf(item)), [key]: e.target.value });
            setConfirming(false);
            setAcknowledged(false);
          }}
        />
      )}
    </label>
  );
  const save = () => {
    if (!current || !cmd.current || !draft) return;
    sent.current = draft;
    void cmd.submit({
      command_id: crypto.randomUUID(),
      ...faqExpectations(cmd.current),
      expected_item_revision: current.revision,
      title: current.title,
      payload: {
        ...current.payload,
        question: { ko: orNull(draft.questionKo), en: orNull(draft.questionEn) },
        answer: { ko: orNull(draft.answerKo), en: orNull(draft.answerEn) },
      },
    });
  };
  return (
    <section aria-label="공유 FAQ 원문 수정" className="space-y-3 rounded-md border p-4">
      <h4 className="font-medium">공유 원문 수정</h4>
      {cmd.error && (
        <p role="alert" className="body-sm">
          {cmd.error}
        </p>
      )}
      {!cmd.current && !cmd.error && <p role="status">현재 공유 원문을 확인하고 있습니다.</p>}
      {!cmd.current && cmd.error && (
        <Button
          variant="outline"
          onClick={() => {
            void cmd.refresh();
          }}
        >
          현재 원문 다시 조회
        </Button>
      )}
      {cmd.current && !current && (
        <p role="alert">이 항목을 공유 원문에서 찾지 못했습니다. 다시 조회해 주세요.</p>
      )}
      {cmd.current && current && draft && (
        <>
          <p className="body-sm text-muted-foreground">
            이 항목은 여러 지점이 함께 쓰는 공유 원문입니다. 저장하면 아래 {affected.length}개
            지점의 FAQ에 모두 반영되며, 한 지점만 따로 고칠 수는 없습니다.
          </p>
          <ul aria-label="이 원문을 쓰는 지점" className="body-sm list-disc pl-5">
            {affected.map((entry) => (
              <li key={entry.id}>{entry.name}</li>
            ))}
          </ul>
          {names.isError && (
            <p className="body-sm text-muted-foreground">
              다른 지점 이름을 조회하지 못했습니다. 지점 수는 위 목록 그대로입니다.
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            {field('questionKo', '질문 · 한국어')}
            {field('questionEn', '질문 · English')}
            {field('answerKo', '답변 · 한국어', true)}
            {field('answerEn', '답변 · English', true)}
          </div>
          {confirming ? (
            <div className="space-y-2" aria-label="변경 영향 확인">
              <p className="body-sm" role="status">
                {affected.length}개 지점에 같은 질문·답변으로 반영됩니다.
              </p>
              <label className="flex items-start gap-2">
                <Checkbox
                  checked={acknowledged}
                  onCheckedChange={(checked) => {
                    setAcknowledged(checked);
                  }}
                />
                <span className="body-sm">위 지점 모두에 반영되는 것을 확인했습니다.</span>
              </label>
              <EditActionBar label="공유 FAQ 저장 동작">
                <Button disabled={!acknowledged || cmd.busy} onClick={save}>
                  모든 지점에 반영해 저장
                </Button>
              </EditActionBar>
            </div>
          ) : (
            <EditActionBar label="공유 FAQ 검토 동작">
              <Button
                variant="outline"
                disabled={!changed || cmd.busy}
                onClick={() => {
                  setConfirming(true);
                }}
              >
                변경 영향 확인
              </Button>
            </EditActionBar>
          )}
          {cmd.attempt && !cmd.attempt.accepted && cmd.error && (
            <p className="body-sm text-muted-foreground">
              저장 결과를 확인하지 못했습니다. 같은 요청을 그대로 다시 보냅니다.
            </p>
          )}
          {cmd.success && (
            <p role="status">
              {verified
                ? '저장 후 공유 원문을 다시 조회해 입력한 내용과 같음을 확인했습니다.'
                : '저장 후 다시 조회한 공유 원문입니다. 입력한 내용과 다를 수 있으니 확인해 주세요.'}
            </p>
          )}
        </>
      )}
      <Button variant="outline" onClick={onClose}>
        닫기
      </Button>
    </section>
  );
}
